/**
 * OtherLocationHistory.jsx
 * Shows every "Other location" attendance punch with its approval resolution status.
 * Calls GET /reports/attendance/travelling-employees with date range + status filter.
 * Accessible to: super_admin / MD / CEO / CMD / CFO / HR / Admin
 */
import React, { useEffect, useState, useCallback } from 'react';
import { MapPin, Clock, CheckCircle2, XCircle, AlertCircle, RefreshCw, ExternalLink } from 'lucide-react';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';
import PageHeader from '../../components/PageHeader';

const STATUS_CONFIG = {
  pending_approval: {
    label: 'Pending',
    cls: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: AlertCircle,
  },
  approved: {
    label: 'Approved',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: CheckCircle2,
  },
  rejected: {
    label: 'Rejected',
    cls: 'bg-rose-50 text-rose-700 border-rose-200',
    icon: XCircle,
  },
};

import { formatDateTime as fmtDateTime } from '../../lib/format';

function StatusChip({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending_approval;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-display font-bold uppercase tracking-wider border ${cfg.cls}`}>
      <Icon size={10} /> {cfg.label}
    </span>
  );
}

function dateNDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function OtherLocationHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(dateNDaysAgo(30));
  const [dateTo, setDateTo] = useState(today());
  const [statusFilter, setStatusFilter] = useState('');    // '' = All

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (statusFilter) params.status = statusFilter;
      const r = await apiGet('/reports/attendance/travelling-employees', params);
      setItems(r.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load other-location punches');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const pendingCount = items.filter(i => i.status === 'pending_approval').length;
  const approvedCount = items.filter(i => i.status === 'approved').length;
  const rejectedCount = items.filter(i => i.status === 'rejected').length;

  return (
    <div data-testid="other-location-history-page">
      <PageHeader
        overline="Reports · HR Attendance"
        title="Other Location Punch History"
        subtitle="All employee attendance punches from 'Other' locations — Pending, Approved, and Rejected. Use this to track what happened after an approval card disappeared from the Approvals Hub."
      />

      {/* Summary chips */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { label: 'Pending', count: pendingCount, cls: 'bg-amber-50 border-amber-200 text-amber-700' },
          { label: 'Approved', count: approvedCount, cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
          { label: 'Rejected', count: rejectedCount, cls: 'bg-rose-50 border-rose-200 text-rose-700' },
        ].map(({ label, count, cls }) => (
          <div key={label} className={`border rounded-sm p-4 ${cls}`} data-testid={`other-loc-count-${label.toLowerCase()}`}>
            <div className="text-2xl font-display font-extrabold tabular-nums">{count}</div>
            <div className="text-[11px] font-display font-bold uppercase tracking-wider mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-wider text-slate-500 mb-1">From</label>
          <input
            type="date" value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="border border-slate-300 rounded-sm px-3 py-1.5 text-sm focus:outline-none focus:border-[#3b4872]"
            data-testid="other-loc-date-from"
          />
        </div>
        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-wider text-slate-500 mb-1">To</label>
          <input
            type="date" value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="border border-slate-300 rounded-sm px-3 py-1.5 text-sm focus:outline-none focus:border-[#3b4872]"
            data-testid="other-loc-date-to"
          />
        </div>
        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-wider text-slate-500 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="border border-slate-300 rounded-sm px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#3b4872]"
            data-testid="other-loc-status-filter"
          >
            <option value="">All ({items.length})</option>
            <option value="pending_approval">Pending ({pendingCount})</option>
            <option value="approved">Approved ({approvedCount})</option>
            <option value="rejected">Rejected ({rejectedCount})</option>
          </select>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-4 py-1.5 border border-[#3b4872] text-[#3b4872] rounded-sm text-xs font-display font-bold hover:bg-indigo-50 disabled:opacity-50"
          data-testid="other-loc-refresh"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
        <div className="ml-auto text-[11px] text-slate-500 font-display">
          {loading ? 'Loading…' : `${items.length} record${items.length !== 1 ? 's' : ''}`}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-xs" data-testid="other-loc-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2.5 font-display font-bold uppercase tracking-wider text-slate-500 text-[10px]">Employee</th>
              <th className="text-left px-4 py-2.5 font-display font-bold uppercase tracking-wider text-slate-500 text-[10px]">Punch</th>
              <th className="text-left px-4 py-2.5 font-display font-bold uppercase tracking-wider text-slate-500 text-[10px]">Location / Reason</th>
              <th className="text-left px-4 py-2.5 font-display font-bold uppercase tracking-wider text-slate-500 text-[10px]">Status</th>
              <th className="text-left px-4 py-2.5 font-display font-bold uppercase tracking-wider text-slate-500 text-[10px]">Resolved By</th>
              <th className="text-left px-4 py-2.5 font-display font-bold uppercase tracking-wider text-slate-500 text-[10px]">Resolved At</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan="7" className="text-center py-12 text-slate-400 text-xs font-display uppercase tracking-widest">
                  No "Other location" punches found for this period.
                </td>
              </tr>
            )}
            {items.map((item, idx) => {
              const resolvedBy = item.approved_by || item.rejected_by || null;
              const resolvedAt = item.approved_at || item.rejected_at || null;
              return (
                <tr key={item.id || idx} className="hover:bg-slate-50/60 transition-colors" data-testid={`other-loc-row-${idx}`}>
                  <td className="px-4 py-3">
                    <div className="font-display font-semibold text-slate-800">{item.name || '—'}</div>
                    <div className="text-[10px] text-slate-400 font-mono mt-0.5">{item.emp_id || ''} {item.department ? `· ${item.department}` : ''}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Clock size={11} className="text-slate-400" />
                      <span className="font-display font-semibold uppercase tracking-wider text-[11px] text-slate-700">
                        {(item.kind || '—').toUpperCase()}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{fmtDateTime(item.time)}</div>
                  </td>
                  <td className="px-4 py-3 max-w-[200px]">
                    <div className="flex items-start gap-1.5">
                      <MapPin size={11} className="text-slate-400 mt-0.5 shrink-0" />
                      <span className="text-slate-700 line-clamp-2">{item.remark || '—'}</span>
                    </div>
                    {item.gps_lat && (
                      <div className="text-[10px] text-slate-400 mt-0.5 font-mono">
                        {Number(item.gps_lat).toFixed(5)}, {Number(item.gps_lng).toFixed(5)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusChip status={item.status} />
                    {item.status === 'rejected' && item.rejection_reason && (
                      <div className="text-[10px] text-rose-700 mt-1 italic max-w-[160px] truncate" title={item.rejection_reason}>
                        "{item.rejection_reason}"
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {resolvedBy || (item.status === 'pending_approval' ? <span className="text-amber-600 text-[10px]">Awaiting action</span> : '—')}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-[11px] whitespace-nowrap">
                    {resolvedAt ? fmtDateTime(resolvedAt) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {item.gps_lat && (
                      <a
                        href={`https://www.google.com/maps?q=${item.gps_lat},${item.gps_lng}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#3b4872] hover:text-[#2c3658]"
                        title="View on Google Maps"
                        data-testid={`other-loc-map-${idx}`}
                      >
                        <ExternalLink size={12} />
                      </a>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
